import type { ApiResponse, ApiError, ApiMeta } from '@/types'
import { useAuthStore } from '@/stores/authStore'

// API base URL - resolved at runtime based on hostname.
// Local dev (localhost): empty string → Vite proxy handles routing.
// Each portal is pinned to the API under its own parent domain, because the session
// cookie is scoped to that parent (COOKIE_DOMAIN). Crossing them silently drops auth.
// Any other host falls back to VITE_API_URL, set at build time.
const API_BY_PORTAL: Record<string, string> = {
  'portal.triple-a.international': 'https://api.triple-a.international',
}

export function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const pinned = API_BY_PORTAL[window.location.hostname]
    if (pinned) return pinned
  }
  // Vercel stored this value with a trailing newline; a raw join would produce
  // 'https://api.example\n/api/proposals'.
  return (import.meta.env.VITE_API_URL || '').trim()
}
const API_BASE_URL = resolveApiBaseUrl()

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code: string,
    public details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

/**
 * Request configuration options
 */
interface RequestConfig extends Omit<RequestInit, 'body'> {
  body?: unknown
  skipAuth?: boolean
  /** Resolve with the Response itself (files); 401/403 are still handled here. */
  raw?: boolean
}

/**
 * Makes an authenticated API request
 * - Sends cookies for session-based auth (credentials: 'include')
 * - Handles 401 (unauthorized) and 403 (forbidden) responses
 * - Returns typed responses using @/types
 */
async function request<T, M = ApiMeta>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<ApiResponse<T, M>> {
  const { body, skipAuth = false, raw = false, ...fetchConfig } = config

  // Build headers
  const headers = new Headers(fetchConfig.headers)
  // FormData: the browser sets the multipart Content-Type with its boundary.
  if (!(body instanceof FormData)) headers.set('Content-Type', 'application/json')

  // Build request URL
  const url = `${API_BASE_URL}${endpoint}`

  try {
    // A malformed url throws here with its own message, so it is not mistaken for a network failure below.
    new URL(url, window.location.href)
    const fetchOptions: RequestInit = {
      ...fetchConfig,
      headers,
      credentials: 'include', // Include cookies for session-based auth
    }

    // Only add body if it exists (avoid undefined in RequestInit)
    if (body !== undefined) {
      fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body)
    }

    // Only a TypeError from fetch() itself is a network failure; the message differs per browser.
    const response = await fetch(url, fetchOptions).catch((e: unknown) => {
      if (e instanceof TypeError) {
        throw new ApiClientError('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาตรวจสอบอินเทอร์เน็ต', 0, 'NETWORK_ERROR')
      }
      throw e
    })

    // A proxy or gateway error page is HTML, so the body can be unparseable.
    const readBody = <B,>() => response.json().catch(() => null) as Promise<B | null>
    const badBody = (status: number) =>
      status >= 500
        ? new ApiClientError('เซิร์ฟเวอร์ไม่ตอบสนอง กรุณาลองอีกครั้ง', status, 'SERVER_ERROR')
        : new ApiClientError('เกิดข้อผิดพลาด กรุณาลองอีกครั้ง', status, 'UNKNOWN_ERROR')

    // Handle 401 Unauthorized
    if (response.status === 401) {
      // Only clear auth state if this was an authenticated request
      // Don't clear on 401s that don't mean the session expired (skipAuth = true: login, company sign, invite verify)
      if (!skipAuth) {
        useAuthStore.getState().clearUser()
        throw new ApiClientError(
          'Session expired. Please log in again.',
          401,
          'UNAUTHORIZED'
        )
      }
      // For login failures, parse the actual error from response
      const errorData = await readBody<ApiResponse<never>>()
      if (!errorData) throw badBody(401)
      throw new ApiClientError(
        errorData.error?.message || 'Invalid credentials',
        401,
        errorData.error?.code || 'INVALID_CREDENTIALS'
      )
    }

    // Handle 403 Forbidden
    if (response.status === 403) {
      // Parse the response to check for PASSWORD_CHANGE_REQUIRED
      const errorData = await readBody<ApiResponse<never>>()
      const errorCode = errorData?.error?.code || 'FORBIDDEN'

      // If password change required, update store and let caller handle redirect
      if (errorCode === 'PASSWORD_CHANGE_REQUIRED') {
        useAuthStore.getState().setForcePasswordChange(true)
        throw new ApiClientError(
          errorData?.error?.message || 'You must change your password before accessing this resource.',
          403,
          'PASSWORD_CHANGE_REQUIRED'
        )
      }

      throw new ApiClientError(
        errorData?.error?.message || 'You do not have permission to perform this action.',
        403,
        errorCode
      )
    }

    if (raw) {
      if (!response.ok) {
        const errorData = await readBody<ApiResponse<never>>()
        if (!errorData) throw badBody(response.status)
        const error = errorData.error
        throw new ApiClientError(error?.message || 'Could not open the file', response.status, error?.code || 'REQUEST_FAILED')
      }
      return { success: true, data: response as T }
    }

    // 204 has no body (DELETE endpoints)
    if (response.status === 204) return { success: true } as ApiResponse<T, M>

    // Parse response
    const data = await readBody<ApiResponse<T, M>>()
    if (!response.ok && !data) throw badBody(response.status)

    // Handle API-level errors
    if (!response.ok || !data?.success) {
      const error = data?.error as ApiError | undefined
      throw new ApiClientError(
        error?.message || 'An unexpected error occurred',
        response.status,
        error?.code || 'UNKNOWN_ERROR',
        error?.details
      )
    }

    return data as ApiResponse<T, M>
  } catch (error) {
    // Re-throw ApiClientError as-is
    if (error instanceof ApiClientError) {
      throw error
    }

    // Handle other errors
    throw new ApiClientError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      0,
      'UNKNOWN_ERROR'
    )
  }
}

/**
 * API client with typed methods
 */
export const api = {
  /** GET a non-JSON body (documents); the caller reads the Response. */
  getRaw: (endpoint: string) => request<Response>(endpoint, { method: 'GET', raw: true }),

  get: <T, M = ApiMeta>(endpoint: string, config?: RequestConfig) =>
    request<T, M>(endpoint, { ...config, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'PATCH', body }),

  delete: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'DELETE' }),
}

export default api
